<template>
  <b-container>
    <b-modal v-model="tableEditVisible" title="edit a table" @ok="onModalOk" :ok-disabled="aiSubmitting" :ok-title="activeTab === 4 ? (aiSubmitting ? 'updating...' : 'update with AI') : 'OK'" size="xl" modal-class="view-editor-modal">
      <template #title>
        edit a table
        <modal-help topic="edit-table" />
      </template>
      <div class="edit-modal-content">
        <b-tabs v-model="activeTab" content-class="tab-body" nav-class="view-editor-tabs" pills>

          <b-tab title="General">
            <div class="tab-section">
              <div class="section-heading">Naming</div>
              <div class="field-row">
                <label class="field-label">
                  unique title
                  <b-form-input v-model="uniqueTitle" placeholder="table unique title" required />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  title
                  <b-form-input v-model="title" placeholder="table title" required />
                </label>
              </div>
            </div>
            <div class="tab-section">
              <div class="section-heading">Data Source</div>
              <div class="data-source-display">
                <span class="data-source-spreadsheet">{{ dataSourceLabel }}</span>
                <span v-if="filterByPageRecord" class="data-source-badge">filtered by page record</span>
              </div>
              <label v-if="pageRecordLink" class="checkbox-label page-record-filter">
                <input type="checkbox" v-model="filterByPageRecord" />
                only show records related to the page's {{ pageRecordLink.pageRecordTitle }} record
              </label>
              <p v-if="pageRecordLink" class="section-desc">this page is opened from a link that carries a {{ pageRecordLink.pageRecordTitle }} record. the view keeps records related through {{ pageRecordLink.columnTitle }}.</p>
            </div>
            <div class="tab-section">
              <div class="section-heading">Realtime</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="realtime" />
                enable realtime updates (data will update automatically when records change)
              </label>
            </div>
            <div class="tab-section">
              <div class="section-heading">Export</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="csvExport" />
                allow export. visitors can download the filtered rows as CSV, text, or JSON.
              </label>
            </div>
            <div class="tab-section">
              <div class="section-heading">Layout</div>
              <div class="field-row">
                <label class="field-label">
                  page size
                  <b-form-input v-model="pageSize" type="number" min="1" placeholder="25" />
                </label>
                <label class="field-label">
                  maximum page size
                  <b-form-input v-model="pageSizeMax" type="number" min="1" placeholder="same as page size" />
                </label>
              </div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="wrapHeaders" />
                wrap column headers
              </label>
              <label class="checkbox-label">
                <input type="checkbox" v-model="hideEmptyColumns" />
                hide columns that are empty in the current rows
              </label>
            </div>
          </b-tab>

          <b-tab title="Columns">
            <button
              v-if="showAddAllColumns"
              type="button"
              @click="addAllSpreadsheetColumns"
              class="add-btn add-all-columns-btn"
            >
              + add all columns from {{ spreadsheetTitle }}
            </button>
            <div class="tab-section" v-if="table.components">
              <p v-if="table.components.length > 1" class="section-desc">drag the handle to change the order of columns in this view.</p>
              <draggable
                v-model="table.components"
                :item-key="columnDragKey"
                handle=".column-drag-handle"
                ghost-class="column-drag-ghost"
                :animation="150"
                :force-fallback="true"
                :fallback-on-body="true"
              >
                <template #item="{ element: component }">
                  <div class="form-edit-item">
                    <DragHandle
                      handle-class="column-drag-handle"
                      :reorder-id="component.uniqueTitle || component.label"
                      @move="moveColumnByKey(component, $event)"
                    />
                    <div class="editable-component" @click="editComponent(table, component)">
                      <div class="component-type">{{component.type}}</div>
                      <div class="component-details" v-if="component.uniqueTitle || component.label || component.childPageUniqueTitle">
                        <span v-if="component.uniqueTitle || component.relatedField" class="component-detail"><span class="detail-label">field:</span> {{ columnFieldLabel(component) }}<span v-if="getColumnType(component.uniqueTitle)" class="column-type-badge">{{getColumnType(component.uniqueTitle)}}</span></span>
                        <span v-if="component.label" class="component-detail"><span class="detail-label">label:</span> {{component.label}}</span>
                        <span v-if="component.childPageUniqueTitle" class="component-detail"><span class="detail-label">links to:</span> {{component.childPageUniqueTitle}}</span>
                        <span v-if="component.linkMode" class="component-detail"><span class="detail-label">mode:</span> {{component.linkMode === 'code' ? 'custom code' : component.linkMode === 'relatedRecord' ? `related record (${component.relationshipColumn})` : component.linkMode === 'static' ? 'static page' : 'this record'}}</span>
                        <span v-if="component.customText" class="component-detail"><span class="detail-label">text:</span> {{component.customText}}</span>
                        <span v-if="component.displayAsButton" class="component-detail"><span class="detail-label">shown as:</span> button</span>
                        <span v-if="component.valueFormatting && component.valueFormatting.type" class="editable-badge">formatted</span>
                        <span v-if="component.editable" class="editable-badge">editable</span>
                        <span v-if="component.disableSorting" class="editable-badge">sorting off</span>
                        <span v-if="component.editable && component.defaultValueRules && component.defaultValueRules.rules && component.defaultValueRules.rules.length" class="editable-badge">{{ component.defaultValueRules.rules.length }} default rule{{ component.defaultValueRules.rules.length > 1 ? 's' : '' }}</span>
                        <span v-if="component.editable && component.submissionRules && component.submissionRules.rules && component.submissionRules.rules.length" class="editable-badge">{{ component.submissionRules.rules.length }} submission rule{{ component.submissionRules.rules.length > 1 ? 's' : '' }}</span>
                      </div>
                    </div>
                    <span
                      @click.stop="openRuleEditor(component)"
                      class="rules-btn"
                      :class="{ 'has-rules': (component.formattingRules && component.formattingRules.rules && component.formattingRules.rules.length) || (component.valueFormatting && component.valueFormatting.type) }"
                      title="Formatting rules - add conditional styling based on field values"
                    ><span class="rules-btn-inner">&#9889;<span class="rules-btn-label">rules</span></span></span>
                    <span @click="removeComponent(table, component)" class="delete">x</span>
                  </div>
                </template>
              </draggable>
              <div @click="createComponent(table)" class="add-btn">
                + add column
              </div>
            </div>
          </b-tab>

          <b-tab title="Filtering">
            <div v-if="pageRecordLink" class="tab-section">
              <div class="section-heading">Page record</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="filterByPageRecord" />
                only show records related to the page's {{ pageRecordLink.pageRecordTitle }} record
              </label>
              <p class="section-desc">this page is opened from a link that carries a {{ pageRecordLink.pageRecordTitle }} record. the view keeps records related through {{ pageRecordLink.columnTitle }}.</p>
            </div>
            <div class="tab-section">
              <label class="checkbox-label">
                <input type="checkbox" v-model="searchEnabled" />
                show a search box. visitors can type text to narrow the records in this view.
              </label>
              <div v-if="searchEnabled" class="search-scope">
                <label class="checkbox-label">
                  <input type="radio" value="view" v-model="searchScope" />
                  search the fields on this view, including fields from related spreadsheets
                </label>
                <label class="checkbox-label">
                  <input type="radio" value="spreadsheet" v-model="searchScope" />
                  search every field on the spreadsheet
                </label>
                <p v-if="searchScope === 'spreadsheet'" class="section-desc search-security-note">
                  Searching every field on the spreadsheet matches columns that are not shown on this view. A visitor can learn that a hidden value exists even though that column stays off the view. Password fields are never searched.
                </p>
              </div>
            </div>
            <div class="tab-section">
              <label class="checkbox-label">
                <input type="checkbox" v-model="userFiltering" />
                allow users to filter records
              </label>
              <label v-if="userFiltering" class="checkbox-label" style="margin-top: 8px;">
                <input type="checkbox" v-model="restrictUserFiltering" />
                restrict filter fields to fields on this view. if this option is unchecked, users can filter on any field or connected field.
              </label>
            </div>
            <div v-if="userFiltering" class="tab-section">
              <div class="section-heading">Default Filters</div>
              <p class="section-desc">these filters will be displayed by default on the component, and users can change them</p>
              <PreFilterBuilder
                :filters="defaultUserFilters"
                :match-mode="defaultUserFilterMatchMode"
                :spreadsheet="spreadsheet"
                :all-spreadsheets="allSpreadsheets"
                :application-unique-title="applicationUniqueTitle"
                @update:filters="defaultUserFilters = $event"
                @update:match-mode="defaultUserFilterMatchMode = $event"
              />
            </div>
            <div class="tab-section">
              <div class="section-heading">Prefilters</div>
              <p class="section-desc">these filters will always be applied to the record set and will not be displayed on the view</p>
              <PreFilterBuilder
                :filters="preFilters"
                :match-mode="preFilterMatchMode"
                :spreadsheet="spreadsheet"
                :all-spreadsheets="allSpreadsheets"
                :application-unique-title="applicationUniqueTitle"
                @update:filters="preFilters = $event"
                @update:match-mode="preFilterMatchMode = $event"
              />
            </div>
            <FilterButtonEditor
              :buttons="filterButtons"
              :allow-multiple="allowMultipleFilterButtons"
              :spreadsheet="spreadsheet"
              :all-spreadsheets="allSpreadsheets"
              :application-unique-title="applicationUniqueTitle"
              @update:buttons="filterButtons = $event"
              @update:allow-multiple="allowMultipleFilterButtons = $event"
            />
          </b-tab>

          <b-tab title="Sorting">
            <div class="tab-section">
              <label class="checkbox-label">
                <input type="checkbox" v-model="disableUserSorting" />
                disable user sorting. column headers will not reorder the records in this view.
              </label>
            </div>
            <div class="tab-section">
              <div class="section-heading">Sort Order</div>
              <p class="section-desc">records in this view are ordered by these columns, from highest precedence to lowest. each column can sort ascending or descending.</p>
              <SortBuilder
                :sorts="sorts"
                :spreadsheet="spreadsheet"
                @update:sorts="sorts = $event"
              />
            </div>
          </b-tab>

          <b-tab title="AI">
            <ViewAiPanel v-model="aiPrompt" :error="aiError || ''" :submitting="aiSubmitting" />
          </b-tab>

        </b-tabs>
      </div>
    </b-modal>
    <selectColumn ref="selectColumnRef"></selectColumn>
    <RuleEditor
      :visible="ruleEditorVisible"
      :rules="ruleEditorRules"
      :value-formatting="ruleEditorValueFormatting"
      :spreadsheet="spreadsheet"
      :all-spreadsheets="allSpreadsheets"
      :column-unique-title="ruleEditorColumnUniqueTitle"
      :column-data-type="ruleEditorColumnDataType"
      :application-unique-title="applicationUniqueTitle"
      @update:visible="ruleEditorVisible = $event"
      @update:rules="onRulesUpdate"
      @update:valueFormatting="onValueFormattingUpdate"
    />
  </b-container>
</template>

<script>

  import axios from 'axios'
  import selectColumn from './cardsselectcolumn.vue'
  import PreFilterBuilder from './lib/PreFilterBuilder.vue'
  import FilterButtonEditor from './lib/FilterButtonEditor.vue'
  import SortBuilder from './lib/SortBuilder.vue'
  import RuleEditor from './lib/RuleEditor.vue'
  import draggable from 'vuedraggable'
  import DragHandle from './DragHandle.vue'
  import { moveListItem } from '../../../shared/list-move.js'
  import { inferFormulaResultType } from '../lib/formula-utils.js'
  import { submitViewAi } from '../lib/view-ai.js'
  import ViewAiPanel from './ViewAiPanel.vue'
  import { buildComponentsFromSpreadsheetColumns } from '../lib/spreadsheet-view-fields.js'
  import { columnDragKey } from '../lib/column-drag-key.js'
  import { relatedViewColumnOptions, findViewColumnOption, viewColumnDataType, assignRelatedViewColumn, relatedColumnCaption } from '../../../shared/related-view-columns.js'
  import { assignMultiselectDisplay } from '../../../shared/multiselect-display.js'
  import { assignViewValueFormatting } from '../../../shared/value-format.js'
  import { assignViewLink } from '../../../shared/view-fields.js'
  import { pageRecordRelationship } from '../lib/page-record-link.js'
  import { viewEditorTabAfterColumnDialog } from '../lib/view-editor-tab.js'

  export default {
    name: `tableEdit`,
    data () {

      return {
        tableEditVisible: false,
        activeTab: 0,
        aiPrompt: ``,
        aiError: ``,
        aiSubmitting: false,
        aiSavedUniqueTitle: ``,
        uniqueTitle: ``,
        title: ``,
        spreadSheetColumnOptions: [],
        spreadSheetUniqueTitle: ``,
        spreadsheet: null,
        allSpreadsheets: [],
        table: {
          components: []
        },
        realtime: false,
        csvExport: false,
        pageSize: '',
        pageSizeMax: '',
        wrapHeaders: false,
        hideEmptyColumns: false,
        userFiltering: false,
        searchEnabled: false,
        searchScope: 'view',
        restrictUserFiltering: false,
        filters: [],
        defaultUserFilters: [],
        defaultUserFilterMatchMode: 'all',
        preFilters: [],
        preFilterMatchMode: 'all',
        filterButtons: [],
        allowMultipleFilterButtons: false,
        sorts: [],
        disableUserSorting: false,
        parentDesigner: null,
        filterByPageRecord: false,
        pageRecordSpreadSheetUniqueTitle: '',
        relationshipColumnTitle: '',
        applicationUniqueTitle: '',
        ruleEditorVisible: false,
        ruleEditorRules: [],
        ruleEditorColumnUniqueTitle: '',
        ruleEditorColumnDataType: 'string',
        ruleEditorValueFormatting: null,
        ruleEditorComponent: null,
        pages: [],
        pageComponents: [],
        currentPageUniqueTitle: ''
      }
    },
    components: {
      selectColumn,
      DragHandle,
      PreFilterBuilder,
      FilterButtonEditor,
      SortBuilder,
      RuleEditor,
      draggable,
      ViewAiPanel
    },
    computed: {
      spreadsheetTitle () {
        return (this.spreadsheet && this.spreadsheet.title) || this.spreadSheetUniqueTitle || 'spreadsheet'
      },
      showAddAllColumns () {
        if (this.table.components && this.table.components.length) return false
        return buildComponentsFromSpreadsheetColumns(this.spreadsheet && this.spreadsheet.columns, 'table').length > 0
      },
      pageRecordLink () {
        return pageRecordRelationship(this.allSpreadsheets, this.spreadSheetUniqueTitle, this.pageRecordSpreadSheetUniqueTitle)
      },
      dataSourceLabel () {
        const title = (this.spreadsheet && this.spreadsheet.title) || this.spreadSheetUniqueTitle || 'unknown'
        if (!this.filterByPageRecord) return title

        const link = this.pageRecordLink
        const pageRecordTitle = link ? link.pageRecordTitle : this.pageRecordSpreadSheetUniqueTitle
        const columnTitle = link ? link.columnTitle : this.relationshipColumnTitle
        let label = title
        if (pageRecordTitle) label += ` — related to page's ${pageRecordTitle} record`
        if (columnTitle) label += ` via ${columnTitle}`
        return label
      }
    },
    methods: {
      moveColumnByKey (component, direction) {

        const list = this.table && this.table.components
        const index = (list || []).indexOf(component)

        moveListItem(list, index, direction)
      },
      getColumnType (uniqueTitle) {
        if (!uniqueTitle) return null
        const related = findViewColumnOption(this.spreadSheetColumnOptions, uniqueTitle)
        if (related && related.relatedFieldType) return related.relatedFieldType
        const systemTypes = { id: 'number', createdAt: 'dateTime', updatedAt: 'dateTime' }
        if (systemTypes[uniqueTitle]) return systemTypes[uniqueTitle]
        if (!this.spreadsheet || !this.spreadsheet.columns) return null
        const col = this.spreadsheet.columns.find(c => c.uniqueTitle === uniqueTitle)
        return col ? col.type : null
      },
      columnFieldLabel (component) {
        return relatedColumnCaption(component) || (component && component.uniqueTitle) || ''
      },
      removeComponent (parentComponent, removeComponent) {

        console.log(`components before!`)
        console.log(JSON.stringify(parentComponent.components))

        parentComponent.components = _.filter(parentComponent.components, (component) => {

          return component !== removeComponent
        })

        console.log(`components after!`)
        console.log(JSON.stringify(parentComponent.components))
      },
      async getSpreadSheet () {

        console.log(`getSpreadSheet! ${this.spreadSheetUniqueTitle}`)

        const spreadsheet = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}`, {
          headers: { 'application-unique-title': this.applicationUniqueTitle }
        })

        this.spreadsheet = spreadsheet.data

        if (!this.spreadsheet.columns) {

          return
        }

        const systemFields = [
          { value: 'id', text: 'ID (system)' },
          { value: 'createdAt', text: 'Created At (system)' },
          { value: 'updatedAt', text: 'Updated At (system)' }
        ]

        this.spreadSheetColumnOptions = [
          ...systemFields,
          ...this.spreadsheet.columns.map((column) => {
            return {
              value: column.uniqueTitle,
              text: column.title
            }
          }),
          ...relatedViewColumnOptions(this.spreadsheet, this.allSpreadsheets)
        ]
      },
      async showEditTableComponent (components, spreadSheetUniqueTitle) {

        this.activeTab = viewEditorTabAfterColumnDialog(this.tableEditVisible, this)
        this.aiPrompt = ''
        this.aiError = ''
        this.aiSubmitting = false
        this.aiSavedUniqueTitle = this.uniqueTitle
        this.table.components = components
        this.spreadSheetUniqueTitle = spreadSheetUniqueTitle

        await this.getSpreadSheet()

        this.tableEditVisible = true
      },
      addAllSpreadsheetColumns () {
        if (!this.showAddAllColumns) return
        const fields = buildComponentsFromSpreadsheetColumns(this.spreadsheet.columns, 'table')
        if (!this.table.components) this.table.components = []
        this.table.components.push(...fields)
      },
      async createComponent (component) {

        this.activeSlot = component

        this.$refs.selectColumnRef.parentEditor = this
        this.$refs.selectColumnRef.spreadSheetColumnOptions = this.spreadSheetColumnOptions
        this.$refs.selectColumnRef.showSelectColumnModal()
      },
      async editComponent (parentComponent, component) {

        this.activeSlot = parentComponent
        this.editingComponent = component

        this.$refs.selectColumnRef.parentEditor = this
        this.$refs.selectColumnRef.spreadSheetColumnOptions = this.spreadSheetColumnOptions
        this.$refs.selectColumnRef.showEditColumnModal(component)
      },
      applyColumnDisplay (component, display) {

        if (!component || !display) return

        component.columnWidth = display.columnWidth || null
        component.emptyText = display.emptyText || ''
        component.summary = display.summary || ''
        component.hidden = !!display.hidden
        assignMultiselectDisplay(component, display.multiselectDisplay)
        // A view format replaces the spreadsheet field's format. An empty one keeps the field format.
        assignViewValueFormatting(component, display.valueFormatting)

        if (component.type !== 'action') return

        component.actionKind = display.actionKind || ''
        component.actionValues = display.actionValues || []
        component.actionRelationship = display.actionRelationship || ''
        component.actionRelatedMode = display.actionRelatedMode || 'update'
        component.actionAutomation = display.actionAutomation || ''
        component.actionWorkflow = display.actionWorkflow || ''
        component.submissionRules = display.submissionRules || { rules: [] }
      },
      editComponentInActiveSlot (selectedColumnOption, label, type, childPageUniqueTitle, customText, editableConfig, linkConfig, disableSorting, display) {

        if (type === 'action') {

          this.editingComponent.type = 'action'
          this.editingComponent.uniqueTitle = display && display.actionId
          this.editingComponent.label = label
          this.applyColumnDisplay(this.editingComponent, display)

          return
        }

        let dataType = viewColumnDataType(this.spreadsheet, selectedColumnOption, this.spreadSheetColumnOptions)

        if (type === `link`) {

          dataType = `link`
        }

        if (type === `custom`) {

          dataType = `dataColumn`
        }

        this.editingComponent.type = dataType
        this.editingComponent.uniqueTitle = `${selectedColumnOption}`
        this.editingComponent.label = label
        this.editingComponent.childPageUniqueTitle = childPageUniqueTitle
        this.editingComponent.customText = customText

        if (linkConfig) assignViewLink(this.editingComponent, linkConfig)
        else {
          this.editingComponent.linkMode = undefined
          this.editingComponent.relationshipColumn = undefined
          delete this.editingComponent.code
          delete this.editingComponent.displayAsButton
        }

        if (editableConfig && editableConfig.editable) {
          this.editingComponent.editable = true
          this.editingComponent.placeholder = editableConfig.placeholder || ``
          this.editingComponent.description = editableConfig.description || ``
          this.editingComponent.submissionRules = editableConfig.submissionRules || { rules: [] }
          this.editingComponent.defaultValueRules = editableConfig.defaultValueRules || { rules: [] }
        } else {
          this.editingComponent.editable = false
          this.editingComponent.placeholder = ``
          this.editingComponent.description = ``
          this.editingComponent.submissionRules = { rules: [] }
          this.editingComponent.defaultValueRules = { rules: [] }
        }

        this.editingComponent.disableSorting = !!disableSorting
        this.applyColumnDisplay(this.editingComponent, display)
        this.stampRelatedColumn(this.editingComponent, selectedColumnOption, type)
      },
      async addComponentToActiveSlot (selectedColumnOption, label, type, childPageUniqueTitle, customText, editableConfig, linkConfig, disableSorting, display) {

        if (type === 'action') {

          this.activeSlot.components.push({
            type: 'action',
            uniqueTitle: display && display.actionId,
            label,
            actionKind: display.actionKind,
            actionValues: display.actionValues || [],
            actionRelationship: display.actionRelationship || '',
            actionRelatedMode: display.actionRelatedMode || 'update',
            actionAutomation: display.actionAutomation || '',
            actionWorkflow: display.actionWorkflow || '',
            submissionRules: display.submissionRules || { rules: [] }
          })

          return
        }

        console.log(`add TABLE COL for~~`)
        console.log(selectedColumnOption)

        let dataType = viewColumnDataType(this.spreadsheet, selectedColumnOption, this.spreadSheetColumnOptions)

        if (type === `link`) {

          dataType = `link`
        }

        if (type === `custom`) {

          dataType = `dataColumn`
        }

        const componentDef = {
          type: dataType,
          uniqueTitle: `${selectedColumnOption}`,
          label,
          childPageUniqueTitle,
          customText
        }

        if (linkConfig) assignViewLink(componentDef, linkConfig)

        if (editableConfig && editableConfig.editable) {
          componentDef.editable = true
          componentDef.placeholder = editableConfig.placeholder || ``
          componentDef.description = editableConfig.description || ``
          componentDef.submissionRules = editableConfig.submissionRules || { rules: [] }
          componentDef.defaultValueRules = editableConfig.defaultValueRules || { rules: [] }
        }

        if (disableSorting) componentDef.disableSorting = true
        this.applyColumnDisplay(componentDef, display)

        this.stampRelatedColumn(componentDef, selectedColumnOption, type)

        // todo different unique title for links, selectedcolumnoption doesnt make sense anymore
        this.activeSlot.components.push(componentDef)
      },
      stampRelatedColumn (component, selectedColumnOption, type) {

        const option = type === `link` || type === `custom`
          ? null
          : findViewColumnOption(this.spreadSheetColumnOptions, selectedColumnOption)

        assignRelatedViewColumn(component, option)
      },
      openRuleEditor (component) {

        this.ruleEditorComponent = component
        this.ruleEditorColumnUniqueTitle = component.uniqueTitle || ''
        this.ruleEditorRules = (component.formattingRules && component.formattingRules.rules) ? component.formattingRules.rules : []
        this.ruleEditorValueFormatting = component.valueFormatting || null

        // Determine the column data type
        let dataType = 'string'
        if (this.spreadsheet && this.spreadsheet.columns) {
          const col = this.spreadsheet.columns.find(c => c.uniqueTitle === component.uniqueTitle)
          if (col) {
            if (col.type === 'formula') {
              dataType = inferFormulaResultType(col.formula)
            } else {
              const typeMap = { text: 'string', number: 'number', dateTime: 'datetime', password: 'string', richText: 'string', relationship: 'relationship', multiselect: 'string', address: 'string', duration: 'number', sequence: 'number', signature: 'signature' }
              dataType = typeMap[col.type] || 'string'
            }
          }
        }
        this.ruleEditorColumnDataType = dataType

        this.ruleEditorVisible = true
      },
      onRulesUpdate (rules) {

        if (this.ruleEditorComponent) {
          this.ruleEditorComponent.formattingRules = { rules }
        }
      },
      onValueFormattingUpdate (valueFormatting) {

        if (this.ruleEditorComponent) {
          this.ruleEditorComponent.valueFormatting = valueFormatting
        }
      },
      columnDragKey,
      async onModalOk (event) {

        const handled = await submitViewAi(this, event, { visibleKey: 'tableEditVisible', tabIndex: 4 })

        if (!handled) return this.save()
      },
      async save () {

        if (!this.parentDesigner) {
          console.warn('parentDesigner is not set, cannot save')
          return
        }

        console.log(`save form with table components!`)
        console.log(this.table.components)

        this.parentDesigner.saveActiveComponent({
          uniqueTitle: this.uniqueTitle,
          title: this.title,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          components: this.table.components,
          userFiltering: this.userFiltering,
          searchEnabled: !!this.searchEnabled,
          searchScope: this.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view',
          restrictUserFiltering: this.restrictUserFiltering,
          defaultUserFilters: this.defaultUserFilters,
          defaultUserFilterMatchMode: this.defaultUserFilterMatchMode,
          preFilters: this.preFilters,
          preFilterMatchMode: this.preFilterMatchMode,
          filterButtons: this.filterButtons,
          allowMultipleFilterButtons: !!this.allowMultipleFilterButtons,
          sorts: this.sorts,
          disableUserSorting: this.disableUserSorting,
          filters: this.filters,
          realtime: this.realtime,
          csvExport: this.csvExport,
          pageSize: this.pageSize === '' ? null : this.pageSize,
          pageSizeMax: this.pageSizeMax === '' ? null : this.pageSizeMax,
          wrapHeaders: !!this.wrapHeaders,
          hideEmptyColumns: !!this.hideEmptyColumns,
          filterByPageRecord: !!this.filterByPageRecord
        })
      }
    }
  }
</script>

<style>
/* ── View Editor Modal ── */
.view-editor-modal .modal-dialog {
  max-width: 760px;
}
.view-editor-modal .modal-content {
  background-color: #1a1a18;
  border: 1px solid #333330;
  border-radius: 8px;
}
.view-editor-modal .modal-header {
  border-bottom: 1px solid #2a2a28;
  padding: 16px 24px;
}
.view-editor-modal .modal-header .modal-title {
  font-size: 1.05em;
  font-weight: 600;
  color: #e8e8e6;
  letter-spacing: 0.02em;
}
.view-editor-modal .modal-body {
  padding: 0 24px 16px;
}
.view-editor-modal .modal-footer {
  border-top: 1px solid #2a2a28;
  padding: 12px 24px;
}

/* ── Tabs ── */
.view-editor-tabs {
  border-bottom: 1px solid #2a2a28 !important;
  padding: 12px 0 0 !important;
  margin-bottom: 0 !important;
  gap: 4px !important;
}
.view-editor-tabs .nav-link {
  color: #7a7a78 !important;
  background: transparent !important;
  border: none !important;
  border-radius: 6px 6px 0 0 !important;
  padding: 8px 18px !important;
  font-size: 0.82em !important;
  font-weight: 500 !important;
  letter-spacing: 0.02em;
  transition: color 0.15s, background 0.15s !important;
}
.view-editor-tabs .nav-link:hover {
  color: #b0b0ae !important;
  background: #232320 !important;
}
.view-editor-tabs .nav-link.active {
  color: #e8e8e6 !important;
  background: #232320 !important;
  border-bottom: 2px solid #9ad12b !important;
}

.tab-body {
  padding: 20px 4px 8px;
}

/* ── Sections inside tabs ── */
.tab-section {
  margin-bottom: 20px;
}
.tab-section:last-child {
  margin-bottom: 8px;
}
.section-heading {
  color: #9ad12b;
  font-size: 0.78em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #2a2a28;
}
.section-desc {
  color: #6a6a67;
  font-size: 0.8em;
  margin-bottom: 10px;
  line-height: 1.5;
}
.search-scope {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
  margin-left: 22px;
}
.search-security-note {
  color: #c4a35a;
  margin-top: 4px;
  margin-bottom: 0;
}

/* ── Fields ── */
.field-row {
  margin-bottom: 14px;
}
.field-label {
  display: block;
  color: #8a8a88 !important;
  font-size: 0.82em !important;
  font-weight: 500;
  margin-bottom: 0;
}
.field-label .form-control {
  margin-top: 4px;
}
.checkbox-label {
  color: #8a8a88 !important;
  font-size: 0.82em !important;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  line-height: 1.5;
}
.checkbox-label input[type="checkbox"] {
  margin-top: 3px;
  accent-color: #9ad12b;
}
.page-record-filter {
  margin-top: 12px;
}

/* ── Edit Modal Content (shared) ── */
.edit-modal-content {
  color: #b0b0ae;
}
.edit-modal-content .form-control {
  background-color: #1e1e1b;
  border-color: #3a3a38;
  color: #e8e8e6;
  border-radius: 5px;
  padding: 6px 10px;
  font-size: 0.9em;
}
.edit-modal-content .form-control::placeholder {
  color: #555550;
}
.edit-modal-content .form-control:focus {
  background-color: #1e1e1b;
  border-color: #9ad12b;
  color: #e8e8e6;
  box-shadow: 0 0 0 0.2rem rgba(154, 209, 43, 0.12);
}
.edit-modal-content .form-select {
  background-color: #1e1e1b;
  border-color: #3a3a38;
  color: #e8e8e6;
  border-radius: 5px;
  font-size: 0.9em;
}
.edit-modal-content .form-select:focus {
  border-color: #9ad12b;
  box-shadow: 0 0 0 0.2rem rgba(154, 209, 43, 0.12);
}

/* ── Component List Items ── */
.form-edit-item {
  display: flex;
  align-items: center;
  font-size: 0.82em;
  border: 1px solid #2a2a28;
  border-radius: 5px;
  padding: 0;
  margin: 6px 0;
  background: #1e1e1b;
  transition: border-color 0.15s;
}
.form-edit-item:hover {
  border-color: #3a3a38;
}

.form-edit-item > span:first-child,
.form-edit-item > .editable-component {
  flex: 1;
  padding: 6px 12px;
}

.form-edit-item > span.column-drag-handle {
  flex: 0 0 28px;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: grab;
  color: #555550;
  border-right: 1px solid #2a2a28;
  border-radius: 5px 0 0 5px;
}
.form-edit-item > span.column-drag-handle:hover {
  color: #9ad12b;
  background-color: #232320;
}
.form-edit-item > span.column-drag-handle:active {
  cursor: grabbing;
}
.column-drag-ghost {
  opacity: 0.45;
  background-color: #232320;
  border: 1px dashed #9ad12b;
}

.editable-component {
  cursor: pointer;
  transition: color 0.15s, background-color 0.15s;
  border-radius: 5px 0 0 5px;
}
.editable-component:hover {
  color: #b8f046 !important;
  background-color: #232320;
}

.component-type {
  color: #9ad12b;
  font-weight: 500;
  font-size: 0.95em;
}

.component-details {
  padding-top: 2px;
  padding-bottom: 2px;
}
.component-detail {
  display: block;
  font-size: 0.85em;
  line-height: 1.5;
  color: #6a6a67;
}
.detail-label {
  color: #555550;
}

.form-edit-item > .delete {
  flex: 0 0 30px;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Delete Button ── */
.delete {
  font-size: 12px !important;
  font-weight: bolder;
  color: #f82471 !important;
  text-shadow: none !important;
  border: none;
  border-left: 1px solid #2a2a28;
  flex: 0 0 30px !important;
  max-width: 30px !important;
  min-width: 30px !important;
  padding: 0 !important;
  text-align: center !important;
  cursor: pointer;
  border-radius: 0 5px 5px 0;
  transition: background 0.15s, color 0.15s;
  background: transparent;
}
.delete:hover {
  background-color: #f82471;
  color: #fff !important;
}

/* ── Add Button ── */
.add-btn {
  color: #9ad12b;
  cursor: pointer;
  font-size: 0.82em;
  border: 1px dashed #3a3a38;
  border-radius: 5px;
  padding: 8px 16px;
  margin: 8px 0;
  max-width: 200px;
  transition: color 0.15s, background-color 0.15s, border-color 0.15s;
}
.add-btn:hover {
  color: #b8f046;
  background-color: #1e1e1b;
  border-color: #9ad12b;
}
.add-all-columns-btn {
  max-width: none;
  display: inline-block;
}

/* ── Rules Button ── */
.rules-btn {
  font-size: 12px !important;
  color: #6a6a67 !important;
  text-shadow: none !important;
  border: none;
  border-left: 1px solid #2a2a28;
  flex: 0 0 30px !important;
  max-width: 30px !important;
  min-width: 30px !important;
  padding: 0 !important;
  text-align: center !important;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  background: transparent;
}
.rules-btn:hover {
  background-color: #1e2e12;
  color: #9ad12b !important;
}
.rules-btn.has-rules {
  color: #9ad12b !important;
  background-color: #1e2e12;
}

/* ── Editable Badge ── */
.editable-badge {
  display: inline-block;
  font-size: 0.8em;
  font-weight: 600;
  color: #9ad12b;
  background: rgba(154, 209, 43, 0.12);
  border: 1px solid rgba(154, 209, 43, 0.3);
  border-radius: 3px;
  padding: 0px 5px;
  margin-left: 6px;
  letter-spacing: 0.03em;
  vertical-align: middle;
  line-height: 1.6;
}

/* ── Column Type Badge ── */
.column-type-badge {
  display: inline-block;
  font-size: 0.8em;
  font-weight: 600;
  color: #b8a0d8;
  background: rgba(138, 100, 200, 0.15);
  border: 1px solid rgba(138, 100, 200, 0.3);
  border-radius: 3px;
  padding: 0px 5px;
  margin-left: 6px;
  letter-spacing: 0.03em;
  vertical-align: middle;
  line-height: 1.6;
}

/* ── Rules Button with Label ── */
.rules-btn-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  line-height: 1;
}
.rules-btn-label {
  font-size: 7px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
  font-weight: 600;
}
.rules-btn {
  min-width: 38px !important;
  max-width: 38px !important;
  flex: 0 0 38px !important;
  padding: 4px 0 !important;
}

/* ── Data Source Display ── */
.data-source-display {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  padding: 8px 12px;
  background: #1e1e1b;
  border: 1px solid #2a2a28;
  border-radius: 5px;
}
.data-source-spreadsheet {
  color: #e8e8e6;
  font-size: 0.9em;
  font-weight: 500;
}
.data-source-badge {
  display: inline-block;
  font-size: 0.75em;
  font-weight: 600;
  color: #6ab0de;
  background: rgba(106, 176, 222, 0.12);
  border: 1px solid rgba(106, 176, 222, 0.3);
  border-radius: 3px;
  padding: 1px 7px;
  letter-spacing: 0.03em;
}

/* ── Prefilter sections ── */
.prefilter-section {
  padding: 8px 0;
}
.prefilter-section h6 {
  color: #d0d0ce;
  font-weight: 600;
  margin-bottom: 4px;
}
.prefilter-desc {
  color: #7a7a78;
  font-size: 0.8em;
  margin-bottom: 8px;
}
</style>
